import i18n from '@/utils/i18n';
import React, { useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {
  AlertKeys,
  useUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { ApplicationType } from '@/contexts/dynamic-config';
import { BadgeLabelList } from './badge-label-list';
import {
  BORDER,
  FONTS,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { BrandPageLink } from '@/components/brand-page/brand-page-link';
import { Button } from '@/components/button';
import {
  DEFAULT_IMAGE_SIZE,
  ItemDetailsImageList,
} from './item-details-image-list';
import { ExpandableText } from '@/components/expandable-text';
import { getParamsFromNameValue } from '@/utils/salt-item';
import { getVisibleQuantity } from '@/screens/new-order/hooks/use-quantity-reducer';
import { GTINRichInfo } from './gtin-rich-info';
import { HeaderBackButton } from '@/components/header-back-button';
import { ItemDetailActions } from './item-detail-actions';
import { ItemDetailMetadataLink } from './item-detail-metadata-link';
import { ItemDetailSelector } from './item-detail-selector';
import { ItemDetailsOrderHistory } from './item-details-order-history';
import { ItemDetailsRecommendedItemList } from './item-details-recommended-item-list';
import { ItemDetailsRelatedItemList } from './item-details-related-item-list';
import { ItemDetailTabSection } from './item-detail-tab-section';
import { ItemDetailTitle } from './item-detail-title';
import { ItemLegalDisclaimer } from './item-legal-disclaimer';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { MultiOrderGuideButton } from './multi-order-guide-button';
import { OrderableItemLabels } from './orderable-item-labels';
import { OrderGuideInfo } from './order-guide-info';
import { OrderGuidePartitionSectionActionSheet } from '@/components/order-guide/order-guide-partition-section-sheet';
import { Props, useItemDetailSheetMarginTop } from '@/utils/item-details';
import { RichInfoSection } from './rich-info-section';
import { SalesRepInfo } from './sales-rep-info';
import { SaltItemRichInfo } from './salt-item-rich-info';
import { SampleRequest } from '@/components/ads/sample-request';
import { TooltipContainer } from '@/components/tooltip-container';
import { TooltipPosition } from '@/enums';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useGetItemDetailsDemoVariantPacks } from '@/hooks/use-get-item-details-demo-variant-packs';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigateToDSRTooling } from '@/hooks/use-dsr-tooling-navigation';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useScrollViewTabsContext } from '@/contexts/scroll-view-tabs-context';

export const ItemDetails: React.FC<Props> = ({
  availableQuantityMap,
  brandPageDetails,
  gtinMetadata,
  inDraggableSheet = false,
  isSkuInCartOrOrderGuide,
  itemInformation,
  ItemMenuButton,
  onChangeSelector,
  onClose,
  onSelectItem,
  orderHistoryItems,
  orderHistoryLoading,
  renderActions,
  saltItemInfo,
  selectedItem,
  setDSRToolingActionSheetItemMetadata,
  setShowTitle,
  showSelector,
  source,
  updateQuantity,
  variantPackMetadata,
}) => {
  const itemSkuUUIDs = useMemo(
    () => selectedItem.metadata?.skus?.map((sku) => sku.variantPackUUID) ?? [],
    [selectedItem.metadata?.skus],
  );
  const skuQuantityState = useItemQuantityState(itemSkuUUIDs);
  const { bottom } = useSafeAreaInsets();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { isSmallLayout } = useLayoutContext();
  const { itemModifiersEnabled } = useItemDisplayOptions();
  const navigateToDSRTooling = useNavigateToDSRTooling();
  const { isInOrderGuide } = useOrderGuideItemsContext();
  const { alertsSeen, setAlertSeen } = useUserSettingsContext();
  const salesRepMode = useSalesRepMode();
  const actionSheetMarginTop = useItemDetailSheetMarginTop();
  const {
    onScroll: tabsContextOnScroll,
    scrollOffset,
    scrollViewRef,
  } = useScrollViewTabsContext();
  const { isGuestMode } = useUserContext();
  const isInOrderInboundsStack = useOrderInboundsStore(
    (state) => state.isInOrderInboundsStack,
  );

  const bottomRowPosition = useRef<number | null>(null);
  const orderHistoryPosition = useRef<number | null>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [
    partitionSelectorVariantPackUUID,
    setPartitionSelectorVariantPackUUID,
  ] = useState<string | null>(null);
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;

  const additionalInfo = variantPackMetadata?.additional_info;
  const { recommendedVariantPacks, relatedVariantPacks } =
    useGetItemDetailsDemoVariantPacks({
      recommendedVariantPackUUIDs:
        variantPackMetadata?.demo_recommended_variant_pack_ids ??
        additionalInfo?.demo_recommended_variant_pack_ids ??
        [],
      relatedVariantPackUUIDs:
        variantPackMetadata?.demo_related_variant_pack_ids ??
        additionalInfo?.demo_related_variant_pack_ids ??
        [],
    });

  const itemSelectorEnabled = useMemo<boolean>(
    () =>
      itemModifiersEnabled &&
      selectedItem!.metadata!.modifierGroups!.length > 0,
    [itemModifiersEnabled, selectedItem],
  );

  const skusToShow = useMemo<ItemListOptionMetadata[]>(() => {
    if (!selectedItem.metadata?.skus) {
      return [];
    }

    return itemSelectorEnabled
      ? // Only show variant packs in order guide or in cart
        selectedItem.metadata.skus.filter(isSkuInCartOrOrderGuide)
      : selectedItem.metadata.skus;
  }, [itemSelectorEnabled, selectedItem, isSkuInCartOrOrderGuide]);

  const tooltipEnabledItemUUID = useMemo<string | null>(() => {
    if (!customOrderGuideEditingEnabled || !selectedItem.metadata) {
      return null;
    }

    const catalogMetadata = (selectedItem.metadata.skus || []).find(
      (skuMetadata) => !isInOrderGuide(skuMetadata.variantPackUUID),
    );

    return catalogMetadata?.itemUUID || null;
  }, [customOrderGuideEditingEnabled, isInOrderGuide, selectedItem]);

  if (!selectedItem.metadata) {
    return null;
  }

  // Show if order guide is editable or at least one variant pack is already
  // in the order guide
  const showOrderGuideButton =
    customOrderGuideEditingEnabled ||
    (!isCatalogLimited &&
      Boolean(
        selectedItem.metadata?.skus.some((sku) =>
          isInOrderGuide(sku.variantPackUUID),
        ),
      ));

  const photoURLs = selectedItem.metadata.photoURLs;

  const itemRichInfoComponent = saltApiIntegrationEnabled ? (
    saltItemInfo ? (
      <SaltItemRichInfo saltItemInfo={saltItemInfo} />
    ) : null
  ) : gtinMetadata ? (
    <GTINRichInfo gtinMetadata={gtinMetadata} />
  ) : null;
  const saltAdditionalInformation =
    saltApiIntegrationEnabled && saltItemInfo
      ? getParamsFromNameValue(
          saltItemInfo.basicInformation.additionalInformation,
        )
      : null;
  const sellSheetURL = saltApiIntegrationEnabled
    ? (saltAdditionalInformation?.sell_sheet_pdf_url ?? null)
    : gtinMetadata?.metadata?.sell_sheet_pdf_url;
  const safetyDataSheetURL =
    saltAdditionalInformation?.safety_data_sheet_pdf_url ?? null;

  const ScrollViewComponent = inDraggableSheet
    ? BottomSheetScrollView
    : ScrollView;

  const altText = [
    selectedItem.metadata.displayName,
    selectedItem.metadata.externalID,
  ]
    .filter(Boolean)
    .join('-');

  return (
    <>
      {itemRichInfoComponent ? (
        <ItemDetailTitle
          isVisible={isHeaderVisible}
          ItemMenuButton={ItemMenuButton}
          onVisibilityChange={setShowTitle}
          title={selectedItem.title}
        />
      ) : null}
      {isSmallLayout ? (
        <View style={styles.menuButtonContainer}>{ItemMenuButton}</View>
      ) : null}
      <ScrollViewComponent
        alwaysBounceVertical={false}
        keyboardShouldPersistTaps="handled"
        onScroll={
          itemRichInfoComponent
            ? (e) => {
                setIsHeaderVisible(
                  e.nativeEvent.contentOffset.y > DEFAULT_IMAGE_SIZE,
                );
                tabsContextOnScroll?.(e);
              }
            : undefined
        }
        ref={scrollViewRef}
        testID="item-details-scroll-view"
      >
        <View
          style={[
            styles.base,
            {
              marginTop: actionSheetMarginTop,
              paddingBottom: Math.max(bottom, SCREEN_PADDING),
            },
          ]}
        >
          {itemSelectorEnabled && showSelector && isSmallLayout ? (
            <View
              style={photoURLs.length > 0 ? styles.backButtonWithImage : null}
            >
              <HeaderBackButton
                containerStyle={styles.backButtonContainer}
                onPress={onChangeSelector}
              />
            </View>
          ) : null}
          {photoURLs.length > 0 ? (
            <View style={styles.imageContainer}>
              <ItemDetailsImageList
                alt={altText}
                imageURLs={photoURLs}
                size={DEFAULT_IMAGE_SIZE}
              />
            </View>
          ) : null}
          <OrderableItemLabels option={selectedItem} />
          {brandPageDetails ? (
            <BrandPageLink
              brandPageDetails={brandPageDetails}
              onNavigate={onClose}
              option={selectedItem}
              source={source}
            />
          ) : null}
          <Text style={[TYPOGRAPHY.h2, FONTS.primarySemiBold]}>
            {selectedItem.title}
          </Text>
          <BadgeLabelList badges={selectedItem.metadata.badges} />
          {selectedItem.metadata.description ? (
            <ItemDetailTabSection title={i18n.t('description')}>
              <ExpandableText
                numberOfLines={4}
                textStyle={[TYPOGRAPHY.description, styles.description]}
                trackingData={{
                  name: 'item_details.description',
                  properties: {
                    variantPackUUID: selectedItem.metadata.variantPackUUID,
                  },
                }}
              >
                {selectedItem.metadata.description}
              </ExpandableText>
            </ItemDetailTabSection>
          ) : null}
          {itemSelectorEnabled && showSelector ? (
            <ItemDetailSelector
              closeSelector={onChangeSelector}
              metadata={selectedItem.metadata}
              onNavigateToBecomeCustomer={onClose}
              scrollTo={(y) => {
                scrollViewRef.current?.scrollTo({ animated: true, y });
              }}
              updateQuantity={updateQuantity}
            />
          ) : (
            <View
              onLayout={(e) => {
                bottomRowPosition.current = e.nativeEvent.layout.y;
              }}
              style={styles.bottomRowComponents}
            >
              {skusToShow.map((sku) => {
                const renderOrderGuideButton = (variantPackUUID: string) => (
                  <TooltipContainer
                    content={i18n.t('add_to_order_guide')}
                    onClose={() => {
                      setAlertSeen(AlertKeys.OrderGuideButton);
                    }}
                    position={TooltipPosition.Left}
                    visible={
                      sku.itemUUID === tooltipEnabledItemUUID &&
                      !alertsSeen[AlertKeys.OrderGuideButton]
                    }
                  >
                    <View
                      // collapsable prevents android from removing the native view and breaking animations
                      collapsable={false}
                    >
                      <MultiOrderGuideButton
                        openPartitionSelector={() => {
                          setPartitionSelectorVariantPackUUID(variantPackUUID);
                        }}
                        variantPackUUID={variantPackUUID}
                      />
                    </View>
                  </TooltipContainer>
                );

                return (
                  <View key={sku.variantPackUUID}>
                    <OrderGuideInfo
                      isLastOrderedMessageVisible={false}
                      isParLevelsVisible
                      orderGuidePartitionUUID={sku.orderGuidePartitionUUID}
                      variantPackUUID={sku.variantPackUUID}
                    />
                    <View style={[styles.bottomRowComponents, BORDER.primary]}>
                      <ItemDetailActions
                        inDraggableSheet={inDraggableSheet}
                        isFocused={false}
                        item={sku}
                        onNavigateToBecomeCustomer={onClose}
                        quantity={getVisibleQuantity(
                          skuQuantityState,
                          sku.variantPackUUID,
                        )}
                        quantityInStock={
                          availableQuantityMap?.[sku.variantPackUUID]
                        }
                        renderActions={renderActions}
                        renderOrderGuideButton={
                          showOrderGuideButton
                            ? renderOrderGuideButton
                            : undefined
                        }
                        updateQuantity={updateQuantity}
                      />
                      {salesRepMode ? (
                        <SalesRepInfo
                          inDraggableSheet={inDraggableSheet}
                          onPress={() => {
                            const didNavigate = navigateToDSRTooling({
                              itemMetadata: sku,
                            });

                            if (!didNavigate) {
                              setDSRToolingActionSheetItemMetadata(sku);
                            } else if (isInOrderInboundsStack) {
                              // prevent action sheet from covering inbound's
                              // dsr tooling screen
                              onClose();
                            }
                          }}
                          variantPackUUID={sku.variantPackUUID}
                        />
                      ) : null}
                    </View>
                  </View>
                );
              })}
              {itemSelectorEnabled ? (
                <Button
                  onPress={onChangeSelector}
                  rounded
                  testID="more-options-button"
                  trackingData={{
                    name: 'item_detail.more_options_button',
                    properties: { itemUUID: selectedItem.metadata.itemUUID },
                  }}
                  type="secondary"
                >
                  {i18n.t('more_options')}
                </Button>
              ) : null}
              {!isGuestMode ? (
                <SampleRequest
                  option={selectedItem}
                  source={source}
                  style={styles.sampleRequestContainer}
                />
              ) : null}
              {orderHistoryLoading ? (
                <ActivityIndicator style={styles.orderHistoryContainer} />
              ) : orderHistoryItems.length ? (
                <View
                  onLayout={(e) => {
                    orderHistoryPosition.current = e.nativeEvent.layout.y;
                  }}
                  style={styles.orderHistoryContainer}
                >
                  <ItemDetailsOrderHistory
                    loading={orderHistoryLoading}
                    onToggle={() => {
                      if (
                        typeof orderHistoryPosition.current === 'number' &&
                        typeof bottomRowPosition.current === 'number'
                      ) {
                        scrollViewRef.current?.scrollTo({
                          animated: true,
                          y:
                            orderHistoryPosition.current +
                            bottomRowPosition.current -
                            scrollOffset,
                        });
                      }
                    }}
                    orderHistoryItems={orderHistoryItems}
                  />
                </View>
              ) : null}
            </View>
          )}
          {recommendedVariantPacks.length ? (
            <RichInfoSection title={i18n.t('you_might_also_like')}>
              <ItemDetailsRecommendedItemList
                items={recommendedVariantPacks}
                onSelect={(item) => {
                  onSelectItem(item, 'item_detail_recommended_items');
                }}
                renderActions={renderActions}
                updateQuantity={updateQuantity}
              />
            </RichInfoSection>
          ) : null}
          {itemRichInfoComponent}
          <ItemDetailMetadataLink
            itemInformation={itemInformation}
            safetyDataSheetURL={safetyDataSheetURL}
            sellSheetURL={sellSheetURL}
            variantPackUUID={String(selectedItem.value)}
          />
          {relatedVariantPacks.length ? (
            <RichInfoSection
              style={styles.similarItemsContainer}
              title={i18n.t('similar_items')}
            >
              <ItemDetailsRelatedItemList
                items={relatedVariantPacks}
                onSelect={(item) => {
                  onSelectItem(item, 'item_detail_related_items');
                }}
              />
            </RichInfoSection>
          ) : null}
          <ItemLegalDisclaimer />
        </View>
      </ScrollViewComponent>
      {partitionSelectorVariantPackUUID ? (
        <OrderGuidePartitionSectionActionSheet
          onClose={() => setPartitionSelectorVariantPackUUID(null)}
          variantPackUUID={partitionSelectorVariantPackUUID}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  backButtonContainer: {
    marginBottom: 16,
    marginLeft: 0,
    paddingRight: 16,
    top: -8,
  },
  backButtonWithImage: {
    marginLeft: SCREEN_PADDING,
    position: 'absolute',
    zIndex: 1,
  },
  base: {
    // keep same padding as default action sheet so scroll bar appears in correct spot
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  bottomRowComponents: {
    marginVertical: 8,
  },
  description: {
    lineHeight: 20,
    paddingTop: 8,
  },
  imageContainer: {
    marginBottom: SCREEN_PADDING,
  },
  menuButtonContainer: {
    paddingTop: Platform.OS === 'web' ? SCREEN_PADDING_SMALL : 0,
  },
  orderHistoryContainer: {
    marginTop: 8,
  },
  sampleRequestContainer: {
    marginBottom: 8,
  },
  similarItemsContainer: {
    marginTop: SCREEN_PADDING,
  },
});
